/**
=========================================================
* Material Dashboard 2 React - v2.1.0
=========================================================

* Product Page: https://www.creative-.com/product/material-dashboard-react
* Copyright 2022 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

/**
  This file is used for controlling the global states of the components,
  you can customize the states for the different components here.
*/

import { createContext, useContext, useReducer, useMemo, useCallback } from "react";
import useMediaQuery from "@mui/material/useMediaQuery";

// prop-types is a library for typechecking of props
import PropTypes from "prop-types";

import { GlobalMDAlertProvider, useGlobalMDAlert } from "./GlobalMDAlertContext";

// Material Dashboard 2 React main context
const MaterialUI = createContext();

// Setting custom name for the context which is visible on react dev tools
MaterialUI.displayName = "MaterialUIContext";

// Material Dashboard 2 React reducer
function reducer(state, action) {
  switch (action.type) {
    case "MINI_SIDENAV": {
      return { ...state, miniSidenav: action.value };
    }
    case "TRANSPARENT_SIDENAV": {
      return { ...state, transparentSidenav: action.value };
    }
    case "WHITE_SIDENAV": {
      return { ...state, whiteSidenav: action.value };
    }
    case "SIDENAV_COLOR": {
      return { ...state, sidenavColor: action.value };
    }
    case "TRANSPARENT_NAVBAR": {
      return { ...state, transparentNavbar: action.value };
    }
    case "FIXED_NAVBAR": {
      return { ...state, fixedNavbar: action.value };
    }
    case "OPEN_CONFIGURATOR": {
      return { ...state, openConfigurator: action.value };
    }
    case "DIRECTION": {
      return { ...state, direction: action.value };
    }
    case "LAYOUT": {
      return { ...state, layout: action.value };
    }
    case "DARKMODE": {
      return { ...state, darkMode: action.value };
    }
    case "OPENSIDE": {
      return { ...state, openSide: action.value };
    }
    
    case "NOMBRENEGOCIO": {
      return { ...state, nombreNegocio: action.value };
    }
    
    case "ACCESOWEB": {
      return { ...state, accesoWeb: action.value };
    }

    case "ROLEWEB": {
      return { ...state, roleWeb: action.value };
    }
    
    case "ROLDETALLES": {
      return { ...state, roleDetalles: action.value };
    }

    case "MODULOS": {
      return { ...state, modulos: action.value };
    }
    
    case "USUARIOLOG": {
      return { ...state, usuarioLog: action.value };
    }
    
    case "CLAVELOG": {
      return { ...state, claveLog: action.value };
    }

    case "ANCHOCODIGOBARRA": {
      return { ...state, anchoCodigoBarra: action.value };
    }

    case "ALTOCODIGOBARRA": {
      return { ...state, altoCodigoBarra: action.value };
    }

    case "MODULO_ACTIVO": {
      return { ...state, moduloActivo: action.value };
    }

    case "DIRECCION": {
      return { ...state, direccion_empresa: action.value };
    }

    case "RNC": {
      return { ...state, rnc_empresa: action.value };
    }

    case "COMPROBANTELECTRONICO": {
      return { ...state, comprobanteelectronico: action.value };
    }

    case "ENVIARMOTORCONTABLE": {
      return { ...state, enviarMotorContable: action.value };
    }


    default: {
      throw new Error(`Unhandled action type: ${action.type}`);
    }
  }
}

// Lee de forma segura el módulo activo persistido en localStorage. Se usa
// para sobrevivir recargas de página y navegación directa por URL.
const leerModuloActivoPersistido = () => {
  try {
    if (typeof window === "undefined" || !window.localStorage) return null;
    const valor = window.localStorage.getItem("moduloActivo");
    return valor && valor !== "null" && valor !== "undefined" ? valor : null;
  } catch (_e) {
    return null;
  }
};

// Material Dashboard 2 React context provider
function MaterialUIControllerProvider({ children }) {
  const initialState = {
    miniSidenav: false,
    transparentSidenav: false,
    whiteSidenav: false,
    sidenavColor: "info",
    transparentNavbar: true,
    fixedNavbar: true,
    openConfigurator: false,
    direction: "ltr",
    layout: "dashboard",
    darkMode: false,
    openSide: true,
    nombreNegocio: "false",
    accesoWeb: false,
    roleWeb: "false",
    roleDetalles: [],
    modulos: "false",
    usuarioLog: "",
    claveLog: "",
    anchoCodigoBarra: 0,
    altoCodigoBarra: 0,
    moduloActivo: leerModuloActivoPersistido(),
    direccion_empresa: "",
    rnc_empresa: "",
    comprobanteelectronico: "N",
    enviarMotorContable: "N",
  };

  const [controller, dispatch] = useReducer(reducer, initialState);

  /** Alineado con breakpoints por defecto de MUI (sm: 600, md: 900). */
  const isMobile = useMediaQuery("(max-width:599.95px)");
  const isMdDown = useMediaQuery("(max-width:899.95px)");

  const controllerWithViewport = useMemo(
    () => ({
      ...controller,
      viewport: {
        isMobile,
        isMdDown,
      },
    }),
    [controller, isMobile, isMdDown]
  );

  const value = useMemo(
    () => [controllerWithViewport, dispatch],
    [controllerWithViewport, dispatch]
  );

  return <MaterialUI.Provider value={value}>{children}</MaterialUI.Provider>;
}

// Material Dashboard 2 React custom hook for using context
function useMaterialUIController() {
  const context = useContext(MaterialUI);
  if (!context) {
    throw new Error(
      "useMaterialUIController should be used inside the MaterialUIControllerProvider."
    );
  }

  return context;
}

// Typechecking props for the MaterialUIControllerProvider
MaterialUIControllerProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

// Context module functions
const setMiniSidenav = (dispatch, value) => dispatch({ type: "MINI_SIDENAV", value });
const setTransparentSidenav = (dispatch, value) => dispatch({ type: "TRANSPARENT_SIDENAV", value });
const setWhiteSidenav = (dispatch, value) => dispatch({ type: "WHITE_SIDENAV", value });
const setSidenavColor = (dispatch, value) => dispatch({ type: "SIDENAV_COLOR", value });
const setTransparentNavbar = (dispatch, value) => dispatch({ type: "TRANSPARENT_NAVBAR", value });
const setFixedNavbar = (dispatch, value) => dispatch({ type: "FIXED_NAVBAR", value });
const setOpenConfigurator = (dispatch, value) => dispatch({ type: "OPEN_CONFIGURATOR", value });
const setDirection = (dispatch, value) => dispatch({ type: "DIRECTION", value });
const setLayout = (dispatch, value) => dispatch({ type: "LAYOUT", value });
const setDarkMode = (dispatch, value) => dispatch({ type: "DARKMODE", value });
const setOpenSide = (dispatch, value) => dispatch({ type: "OPENSIDE", value });
const setNombrenegocio = (dispatch, value) => dispatch({ type: "NOMBRENEGOCIO", value });
const setAccesoweb = (dispatch, value) => dispatch({ type: "ACCESOWEB", value });
const setRoleweb = (dispatch, value) => dispatch({ type: "ROLEWEB", value });
const setRoleDetalles = (dispatch, value) => dispatch({ type: "ROLDETALLES", value });
const setModulos = (dispatch, value) => dispatch({ type: "MODULOS", value });
const setUsuarioLog = (dispatch, value) => dispatch({ type: "USUARIOLOG", value });
const setClaveLog = (dispatch, value) => dispatch({ type: "CLAVELOG", value });
const setAnchoCodigoBarra = (dispatch, value) => dispatch({ type: "ANCHOCODIGOBARRA", value });
const setAltoCodigoBarra = (dispatch, value) => dispatch({ type: "ALTOCODIGOBARRA", value });
const setDireccionEmpresa = (dispatch, value) => dispatch({ type: "DIRECCION", value });
const setRncEmpresa = (dispatch, value) => dispatch({ type: "RNC", value });
const setComprobanteelectronico = (dispatch, value) => dispatch({ type: "COMPROBANTELECTRONICO", value });
const setModuloActivo = (dispatch, value) => {
  try {
    if (typeof window !== "undefined" && window.localStorage) {
      if (value === null || value === undefined || value === "") {
        window.localStorage.removeItem("moduloActivo");
      } else {
        window.localStorage.setItem("moduloActivo", String(value));
      }
    }
  } catch (_e) {
    // localStorage no disponible (SSR / modo privado): se ignora.
  }
  dispatch({ type: "MODULO_ACTIVO", value: value || null });
};
const setEnviarMotorContable = (dispatch, value) => dispatch({ type: "ENVIARMOTORCONTABLE", value });

/**
 * Objeto empresa para APIs de productos (id_empresa), alineado con login / barra de empresa.
 */
export function empresaProductosDesdeContexto(nombreNegocio) {
  if (nombreNegocio && typeof nombreNegocio === "object" && nombreNegocio !== null) {
    return nombreNegocio;
  }

  const rawDb =
    typeof nombreNegocio === "string" && nombreNegocio.trim() && nombreNegocio !== "false"
      ? nombreNegocio.trim()
      : "";

  let persisted = null;
  try {
    if (typeof window !== "undefined") {
      const raw = window.localStorage.getItem("empresa");
      if (raw) persisted = JSON.parse(raw);
    }
  } catch (_e) {
    persisted = null;
  }

  if (persisted && typeof persisted === "object") {
    const db = persisted.db_empresa ?? persisted.db ?? rawDb;
    if (!db) return null;
    return { ...persisted, db_empresa: db };
  }

  if (rawDb) return { db_empresa: rawDb };
  return null;
}

/**
 * Fecha de emisión para el QR DGII: siempre `dd-mm-yyyy`, tomada de la firma
 * (`dd-mm-yyyy HH:MM:SS`) o, si no hay firma, del calendario local (no UTC).
 */
export function fechaEmisionDdMmYyyy(valor) {
  const raw = String(valor || "").trim();
  const dmy = raw.match(/^(\d{2})[/-](\d{2})[/-](\d{4})/);
  if (dmy) return `${dmy[1]}-${dmy[2]}-${dmy[3]}`;
  const iso = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) return `${iso[3]}-${iso[2]}-${iso[1]}`;
  const d = new Date();
  return `${String(d.getDate()).padStart(2, "0")}-${String(d.getMonth() + 1).padStart(2, "0")}-${d.getFullYear()}`;
}

function fechaLocalYYYYMMDD() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function payloadConFechaEmision(origen, fechaIso) {
  if (!origen || typeof origen !== "object") return origen;
  const siguiente = { ...origen, fecha: fechaIso };
  if (origen.factura && typeof origen.factura === "object") {
    siguiente.factura = { ...origen.factura, fecha: fechaIso };
  }
  return siguiente;
}

function reescribirFechaEmisionEnUrl(qrUrl, fechaEmision) {
  if (!qrUrl || !fechaEmision) return qrUrl || "";
  try {
    const url = new URL(qrUrl);
    if (url.searchParams.has("FechaEmision")) {
      url.searchParams.set("FechaEmision", fechaEmision);
    }
    return url.toString();
  } catch (_e) {
    return String(qrUrl).replace(/FechaEmision=[^&]*/i, `FechaEmision=${fechaEmision}`);
  }
}

function soloDigitosRnc(valor) {
  return String(valor || "").replace(/\D/g, "");
}

/**
 * URL de consulta de timbre DGII con fecha de firma y código de seguridad.
 * Estos datos solo existen después de firmar (`emitirEcfDgii`); el Excel no los trae.
 */
function armarUrlQrDesdeEmision(payload, data) {
  const fechaFirma = String(data.fecha_firma || "");
  const codigo = String(data.codigo_seguridad || "");
  if (!fechaFirma && !codigo) return "";
  const fechaEmision = fechaEmisionDdMmYyyy(fechaFirma);
  // La emisión devuelve el ambiente del tenant (varinicial.ambienteecf).
  const ambiente = String(data.ambiente || process.env.REACT_APP_DGII_AMBIENTE || "certecf");
  const encf = String(data.encf || payload.comprobante || "").toUpperCase();
  const rncEmisor = soloDigitosRnc(payload.rncemisor || payload.rnc_empresa);
  const rncComprador = soloDigitosRnc(payload.rnc);
  const rncCompradorValido =
    rncComprador &&
    rncComprador !== "999999" &&
    (rncComprador.length === 9 || rncComprador.length === 11);
  const monto = Number(data.monto_total || 0).toFixed(2);
  const esConsumo = encf.slice(0, 3) === "E32";
  if (esConsumo && Number(data.monto_total || 0) < 250000) {
    return (
      `https://fc.dgii.gov.do/${ambiente}/consultatimbrefc?` +
      `rncemisor=${encodeURIComponent(rncEmisor)}` +
      `&encf=${encodeURIComponent(encf)}` +
      `&montototal=${encodeURIComponent(monto)}` +
      `&codigoseguridad=${encodeURIComponent(codigo)}`
    );
  }
  const partes = [
    `RncEmisor=${encodeURIComponent(rncEmisor)}`,
    ...(rncCompradorValido ? [`RncComprador=${encodeURIComponent(rncComprador)}`] : []),
    `ENCF=${encodeURIComponent(encf)}`,
    `FechaEmision=${encodeURIComponent(fechaEmision)}`,
    `MontoTotal=${encodeURIComponent(monto)}`,
    `FechaFirma=${encodeURIComponent(fechaFirma)}`,
    `CodigoSeguridad=${encodeURIComponent(codigo)}`,
  ];
  return `https://ecf.dgii.gov.do/${ambiente}/ConsultaTimbre?${partes.join("&")}`;
}

/**
 * Procedimiento centralizado para guardar una factura. Se movió aquí (desde la
 * pantalla de FacturaPOS) para que distintas pantallas reutilicen la misma
 * lógica de envío del payload al endpoint del backend (facturabackend.py).
 *
 * @param {string} servidor URL base del backend.
 * @param {object} body Payload completo de la factura (id_empresa, factura, etc.).
 * @returns {Promise<{ok: boolean, data?: object, error?: string}>}
 */
export async function guardarFacturaBackend(servidor, body) {
  try {
    const fechaIso = fechaLocalYYYYMMDD();
    const payload = payloadConFechaEmision(body, fechaIso);
    const response = await fetch(`${servidor}/api/facturaselectronicadgii_guardarfactura`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    if (response.ok) {
      const data = await response.json();
      return { ok: true, data: { ...data, fecha: fechaEmisionDdMmYyyy(fechaIso) } };
    }
    const errorData = await response.json().catch(() => ({}));
    return { ok: false, error: errorData.Error || "Error al guardar la factura" };
  } catch (_e) {
    return { ok: false, error: "Error de conexión al guardar factura" };
  }
}

/**
 * Envía el payload del comprobante fiscal electrónico (e-CF) al endpoint de la
 * DGII (facturaselectronicadgiibackend.py), que responde con el Excel del e-CF.
 *
 * El backend responde con el Excel del e-CF en el cuerpo y, en cabeceras, la fecha
 * de validez del NCF (`X-Fecha-Valida`) y un QR preliminar (`X-QR-URL`) SIN código
 * de seguridad ni fecha de firma. Esos datos solo salen de `emitirEcfDgii` y son
 * los que FacturaPOS / la nota de crédito deben usar al imprimir.
 *
 * @param {string} servidor URL base del backend.
 * @param {object} payload Payload del e-CF (rnc, comprobante, items, pago, ...).
 * @returns {Promise<{ok: boolean, blob?: Blob, filename?: string, fechaValida?: string, qrUrl?: string, fecha?: string, error?: string}>}
 */
export async function enviarPayloadFacturaElectronica(servidor, payload) {
  try {
    const fechaIso = fechaLocalYYYYMMDD();
    const response = await fetch(`${servidor}/api/facturaselectronicasdgii_generarexcel`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payloadConFechaEmision(payload, fechaIso)),
    });
    if (response.status === 200) {
      const blob = await response.blob();
      const disposition = response.headers.get("Content-Disposition") || "";
      let filename = "factura_ecf.xlsx";
      const match = disposition.match(/filename="?([^"]+)"?/);
      if (match && match[1]) filename = match[1];
      const fechaValida = response.headers.get("X-Fecha-Valida") || "";
      const qrUrl = reescribirFechaEmisionEnUrl(
        response.headers.get("X-QR-URL") || "",
        fechaEmisionDdMmYyyy(fechaIso)
      );
      return { ok: true, blob, filename, fechaValida, qrUrl, fecha: fechaEmisionDdMmYyyy(fechaIso) };
    }
    const data = await response.json().catch(() => ({}));
    return { ok: false, error: data.Error || "No se pudo generar el comprobante electrónico" };
  } catch (_e) {
    return { ok: false, error: "Error de conexión al generar el comprobante electrónico" };
  }
}

/**
 * Genera el e-CF en XML firmado (fases 1-2 DGII): mismo payload que el Excel
 * más los datos del emisor. Devuelve el XML, el código de seguridad del QR y
 * el resultado de la validación XSD. Si el tenant no tiene certificado
 * cargado, el backend responde 400 con el mensaje correspondiente.
 */
export async function generarXmlFirmadoEcf(servidor, payload) {
  try {
    const response = await fetch(`${servidor}/api/fiscal_generarxmlfirmado`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payloadConFechaEmision(payload, fechaLocalYYYYMMDD())),
    });
    const data = await response.json().catch(() => ({}));
    if (response.ok) {
      const fecha = fechaEmisionDdMmYyyy(data.fecha_firma);
      return { ok: true, ...data, fecha };
    }
    return { ok: false, error: data.Error || "No se pudo generar el XML firmado del e-CF" };
  } catch (_e) {
    return { ok: false, error: "Error de conexión al generar el XML firmado" };
  }
}

/**
 * Emite el e-CF (fase 3 DGII): firma, registra en ecf_emitidos y lo envía a
 * la DGII (consumo <250k va como resumen RFCE; el resto devuelve TrackId).
 * Si la DGII está caída la venta no se bloquea: el comprobante queda en
 * `pendiente_envio` y se reenvía luego con /api/fiscal_reintentarenvios.
 *
 * Devuelve los datos de impresión del comprobante (igual que usa FacturaPOS):
 * `qrUrl` con FechaFirma + CodigoSeguridad, `fecha_firma`, `codigo_seguridad`
 * y `monto_total`.
 */
export async function emitirEcfDgii(servidor, payload) {
  try {
    const fechaIso = fechaLocalYYYYMMDD();
    const response = await fetch(`${servidor}/api/fiscal_emitirecf`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payloadConFechaEmision(payload, fechaIso)),
    });
    const data = await response.json().catch(() => ({}));
    if (response.ok) {
      const fechaFirma = String(data.fecha_firma || "");
      const codigoSeguridad = String(data.codigo_seguridad || "");
      const fecha = fechaEmisionDdMmYyyy(fechaFirma);
      const qrUrl = armarUrlQrDesdeEmision(payload || {}, data);
      return {
        ok: true,
        ...data,
        fecha,
        qrUrl,
        fecha_firma: fechaFirma,
        codigo_seguridad: codigoSeguridad,
        monto_total: data.monto_total ?? null,
      };
    }
    return { ok: false, error: data.Error || "No se pudo emitir el e-CF" };
  } catch (_e) {
    return { ok: false, error: "Error de conexión al emitir el e-CF" };
  }
}

/**
 * Revierte una factura cuyo e-CF fue rechazado por la DGII: pone en 0 todos
 * los valores y restaura inventario + productoserializado.
 */
export async function revertirFacturaRechazada(servidor, body) {
  try {
    const response = await fetch(`${servidor}/api/facturaselectronicadgii_revertirfactura_rechazada`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    const data = await response.json().catch(() => ({}));
    if (response.ok) {
      return { ok: true, ...data };
    }
    return { ok: false, error: data.Error || "No se pudo revertir la factura rechazada" };
  } catch (_e) {
    return { ok: false, error: "Error de conexión al revertir la factura rechazada" };
  }
}

/**
 * Pone en 0 una nota de crédito cuyo e-CF fue rechazado o quedó en error,
 * para liberar el monto disponible de la factura de origen.
 */
export async function revertirNotaCreditoRechazada(servidor, body) {
  try {
    const response = await fetch(`${servidor}/api/facturabackend_revertirnotacredito_rechazada`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    const data = await response.json().catch(() => ({}));
    if (response.ok) {
      return { ok: true, ...data };
    }
    return { ok: false, error: data.Error || "No se pudo revertir la nota de crédito" };
  } catch (_e) {
    return { ok: false, error: "Error de conexión al revertir la nota de crédito" };
  }
}

export function useProductosMaterialContext() {
  const [controller] = useMaterialUIController();
  const { nombreNegocio } = controller;
  const { showAlert } = useGlobalMDAlert();

  const showSuccess = useCallback(
    (message) => showAlert({ message, color: "success", autoHideDuration: 3000 }),
    [showAlert]
  );
  const showError = useCallback(
    (message) => showAlert({ message, color: "error", autoHideDuration: 4000 }),
    [showAlert]
  );
  const showWarning = useCallback(
    (message) => showAlert({ message, color: "warning", autoHideDuration: 4000 }),
    [showAlert]
  );

  const selectedCompany = useMemo(() => empresaProductosDesdeContexto(nombreNegocio), [nombreNegocio]);

  return { controller, selectedCompany, showSuccess, showError, showWarning };
}


export {
  MaterialUIControllerProvider,
  GlobalMDAlertProvider,
  useMaterialUIController,
  useGlobalMDAlert,
  setMiniSidenav,
  setTransparentSidenav,
  setWhiteSidenav,
  setSidenavColor,
  setTransparentNavbar,
  setFixedNavbar,
  setOpenConfigurator,
  setDirection,
  setLayout,
  setDarkMode,
  setOpenSide,
  setNombrenegocio,
  setAccesoweb,
  setRoleweb,
  setRoleDetalles,
  setModulos,
  setUsuarioLog,
  setClaveLog,
  setAnchoCodigoBarra,
  setAltoCodigoBarra,
  setModuloActivo,
  setDireccionEmpresa,
  setRncEmpresa,
  setComprobanteelectronico,
  setEnviarMotorContable,
};
